import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Mail, RefreshCw, FileSpreadsheet } from "lucide-react";

// V1.31 · Historial de CIRs generados. Consulta /api/cir/history.
export default function CirHistoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cir/history", { params: { limit: 100 } });
      setItems(data?.documents || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error cargando historial");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadBlob = async (url, filename) => {
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = objUrl; a.download = filename; a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Descarga fallida");
    }
  };

  const fmtDateExam = (ddmmyyyy) => {
    if (!/^\d{8}$/.test(ddmmyyyy || "")) return ddmmyyyy;
    return `${ddmmyyyy.slice(0, 2)}/${ddmmyyyy.slice(2, 4)}/${ddmmyyyy.slice(4)}`;
  };
  const fmtGen = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-3" data-testid="cir-history-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">Historial de CIRs generados</h3>
        <button onClick={load} disabled={loading} className="btn-secondary" data-testid="cir-history-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {items.length === 0 && !loading && (
        <div className="card-surface p-6 text-center text-sm text-neutral-500 italic">
          Aún no se ha generado ningún CIR.
        </div>
      )}

      {items.length > 0 && (
        <div className="card-surface overflow-x-auto" data-testid="cir-history-table">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "130px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "180px" }} />
              <col />
              <col style={{ width: "110px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "200px" }} />
            </colgroup>
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-xs uppercase text-neutral-600">
                <th className="px-4 py-3 font-semibold">Fecha examen</th>
                <th className="px-4 py-3 font-semibold">Código</th>
                <th className="px-4 py-3 font-semibold">Generado</th>
                <th className="px-4 py-3 font-semibold">Usuario</th>
                <th className="px-4 py-3 font-semibold text-center">Profesores</th>
                <th className="px-4 py-3 font-semibold text-center">Alumnos</th>
                <th className="px-4 py-3 font-semibold text-right">Descargas</th>
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr key={doc.id} className="border-b border-neutral-100 hover:bg-orange-50/30 h-[56px]" data-testid={`cir-history-row-${doc.id}`}>
                  <td className="px-4 py-3 font-mono align-middle whitespace-nowrap">{fmtDateExam(doc.exam_date)}</td>
                  <td className="px-4 py-3 font-mono font-semibold align-middle truncate">{doc.cir_code}</td>
                  <td className="px-4 py-3 text-xs text-neutral-600 align-middle whitespace-nowrap">{fmtGen(doc.generated_at)}</td>
                  <td className="px-4 py-3 text-xs text-neutral-700 align-middle truncate" title={doc.generated_by || ""}>{doc.generated_by || "—"}</td>
                  <td className="px-4 py-3 text-center align-middle">{doc.professors_count ?? "—"}</td>
                  <td className="px-4 py-3 text-center align-middle">{doc.students_count ?? "—"}</td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => downloadBlob(`/cir/${doc.id}/download-ods`, doc.filename || `CIR${doc.exam_date}_${doc.cir_code}.ods`)}
                        className="btn-secondary h-8 px-2 text-xs"
                        title="Descargar ODS"
                        data-testid={`cir-history-download-ods-${doc.id}`}
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" /> ODS
                      </button>
                      <button
                        onClick={() => downloadBlob(`/cir/${doc.id}/download-eml`, `correo_CIR${doc.exam_date}_${doc.cir_code}.eml`)}
                        className="btn-secondary h-8 px-2 text-xs"
                        title="Descargar EML"
                        data-testid={`cir-history-download-eml-${doc.id}`}
                      >
                        <Mail className="h-3.5 w-3.5" /> EML
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
